import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState } from 'recoil'
import { listPointState } from 'store/buildingStore'
import {
  currentAnnotation,
  currentPovState,
  povPerPointState
} from 'store/krpanoStore'
import viewerLoadingState from 'store/viewerloadingState'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import {
  Annotation,
  FloorMapElement,
  Point,
  SenceData,
  SenceDataResponse
} from 'types/building'
import { Direction, KrpanoInitProps, Stairway } from 'types/krpano'
import Utils from 'utils'
import Krpano from 'utils/krpano'

export default function useViewerKrpano() {
  const { id } = useParams()
  const { t } = useTranslation()
  const [points] = useRecoilState(listPointState)
  const [searchParams] = useSearchParams()
  const urlH = searchParams.get('hlookat')
  const urlV = searchParams.get('vlookat')
  const urlZ = searchParams.get('fov')
  const isAnnotationDisplay = searchParams.get('is_display')
  const urlSubimage = searchParams.get('subimage')

  const { navigate, getBuiildingType } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [viewerLoading, setViewerLoading] = useRecoilState(viewerLoadingState)

  const [, setCurrentSelected] = useRecoilState(currentAnnotation)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)
  const isFirstLoadingSubimage = useRef(true)

  const [currentHgap, setCurrentHgap] = useState(0)

  // function get all data for create scene
  const { trigger: getSenceData } = useMutation(`v1/points/file-info`)

  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )

  const { data: mapItems, isValidating: isValidatingMapItems } =
    useSWR<FloorMapElement>(
      currentViewer.selectedFloor
        ? {
            url: 'v1/points',
            params: {
              buildingId: id,
              floorId: currentViewer.selectedFloor
            }
          }
        : null,
      {
        revalidateOnMount: false
      }
    )
  const { trigger, isMutating } = useMutation('v1/annotations')

  // handle redirect to next point
  useEffect(() => {
    const handleRedirect = (item: any, clickh: any, clickv: any) => {
      const ponitSelected = mapItems?.points?.find((point) => point.id === item)

      if (ponitSelected?.image360) {
        const krpano = document.getElementById('embedpano-viewer') as any
        Krpano.LoadCheckingViewerMode(() => {
          const h = krpano?.get('view.hlookat')
          const v = krpano?.get('view.vlookat')
          const z = krpano?.get('view.fov')

          const urlToUpdate = new URL(window.location.href)
          urlToUpdate.searchParams.set('hlookat', h)
          urlToUpdate.searchParams.set('vlookat', v)
          urlToUpdate.searchParams.set('fov', z)
          urlToUpdate.searchParams.delete('subimage')

          // statis fpr display all anotation or hidden all anotation
          const isDisplay = urlToUpdate.searchParams.get('is_display')

          if (isDisplay) {
            urlToUpdate.searchParams.set('is_display', isDisplay)
          }

          window.history.pushState({}, '', urlToUpdate.toString())

          const currentRadarHGap =
            Number(currentViewer.pointDetail?.initView?.radarHGap) || 0
          const targetRadarHGap = Number(ponitSelected.initView?.radarHGap) || 0
          const hgapOffset = currentRadarHGap - targetRadarHGap

          krpano.call(`
            hotspot['arrow-${item}'].getcenter(${clickh},${clickv});
            if(${clickv} GT -50 AND ${clickv} LT +50 AND 1000 GT 120, set(${clickv},0));
            set3dtransition("sphere", ${clickh}, ${clickv}, (min(1000,200)), 0, ${hgapOffset}, 0.0, 0.5, easeoutquad);
            `)

          setCurrentPov({
            h: Number(h),
            v: Number(v),
            z
          })
          setCurrentViewer((prev) => {
            return {
              ...prev,
              selectedFloor: ponitSelected.floorId || prev.selectedFloor,
              selectedImage: ponitSelected.image360?.id,
              selectedPoint: ponitSelected.id,
              selectedArea: `${ponitSelected.areaId}`,
              isClickToDirection: true,
              isClickToNextFloor: true,
              isSelectPointInLeftMenu: false
            }
          })
        })
      } else {
        notification.error({
          key: 'redirect',
          message: '画像が設定されていません'
        })
      }
    }

    // Only set the function if it doesn't exist or if dependencies changed
    const depsKey = `${mapItems?.points?.length}-${currentHgap}`
    if (
      !(window as any).handle_redirect ||
      (window as any).handle_redirect.depsKey !== depsKey
    ) {
      ;(window as any).handle_redirect = handleRedirect
      ;(window as any).handle_redirect.depsKey = depsKey
    }

    return () => {
      // Only cleanup if component unmounts, not on every re-render
      if ((window as any).handle_redirect?.depsKey === depsKey) {
        delete (window as any).handle_redirect
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapItems?.points?.length, currentHgap])

  // handle click annotation
  const handleClickAnnotation = () => {
    ;(window as any).handle_click_annotation = (annotationId: string) => {
      !isMutating &&
        trigger(
          {
            method: 'get',
            data: {
              building_id: id,
              floor_id: currentViewer.selectedFloor,
              point_id: currentViewer.selectedPoint
            },
            query: [annotationId]
          },
          {
            onSuccess: (data) => {
              setCurrentSelected(data)
            },
            onError: (error) => {
              setCurrentSelected((prev) => ({
                ...prev,
                isMutating: false
              }))
              if (
                error.response?.data?.error?.key === 'annotation_not_exists'
              ) {
                const krpano = document.getElementById(
                  'embedpano-viewer'
                ) as any
                krpano.call(`remove_annotation(${annotationId})`)
                notification.error({
                  key: 'annotationConflictErr',
                  message: t('translation.deletedAnnotationNotice')
                })
                return
              }
              Utils.handleErrorNavigate(error, navigate)
            }
          }
        )
    }
  }

  // handle change floor
  const handleChangeFloor = () => {
    ;(window as any).handle_change_view_floor = (
      stair_id: string,
      targetFloor: string,
      targetPoint: string,
      clickh: any,
      clickv: any,
      areaId: string
    ) => {
      const embedpano = document.getElementById('embedpano-viewer') as any
      if (!building?.floors.find((item) => item.id === Number(targetFloor))) {
        notification.error({
          key: 'stair_error',
          message: t('building.destinationNotFound')
        })
        embedpano.call(`removehotspot('stair-${stair_id}')`)
        return
      }

      const h = embedpano?.get('view.hlookat')
      const v = embedpano?.get('view.vlookat')
      const z = embedpano?.get('view.fov')

      const urlToUpdate = new URL(window.location.href)
      urlToUpdate.searchParams.set('floor', targetFloor)
      urlToUpdate.searchParams.set('point', targetPoint)
      if (areaId) {
        urlToUpdate.searchParams.set('area', areaId)
      } else {
        urlToUpdate.searchParams.delete('area')
      }
      urlToUpdate.searchParams.set('hlookat', h)
      urlToUpdate.searchParams.set('vlookat', v)
      urlToUpdate.searchParams.set('fov', z)
      urlToUpdate.searchParams.set('is_click_to_next_floor', 'true')
      urlToUpdate.searchParams.delete('subimage')

      window.location.href = urlToUpdate.toString()
    }
  }

  const addPropertiesToKrpano = useCallback(
    (
      krpano: any,
      annotations?: Annotation[],
      directions?: Direction[],
      stairways?: Stairway[],
      tripodUrl?: string
    ) => {
      annotations &&
        Krpano.AddAnnotation(
          krpano,
          annotations,
          isAnnotationDisplay,
          false,
          false
        )
      // add tripod logo

      Krpano.AddTripodLogo(krpano, tripodUrl)

      directions && Krpano.AddDirectionArrow(krpano, directions)
      stairways && Krpano.AddStairway(krpano, stairways)
    },
    [isAnnotationDisplay]
  )

  const krpanoOnreadyCallback = async (
    krpano: any,
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    tripodUrl?: string,
    pointIdOverride?: string,
    selectedImageIdOverride?: number
  ) => {
    const { h, v, z, previewUrl, tilesUrl, multires, imageUrl } =
      krpanoInitProps

    const varString = `layer[set_first_view_button].visible=true`
    const currentH = viewerLoading.firstLoading && urlH ? urlH : h
    const currentV = viewerLoading.firstLoading && urlV ? urlV : v
    const currentZ = viewerLoading.firstLoading && urlZ ? urlZ : z

    krpano.set('var_string', varString)
    krpano.call(
      `loadpano('/krpano/xml-assets/pano_view.xml?v=1.0.1', get(var_string), IGNOREKEEP, BLEND(1))`
    )

    const sceneName = Krpano.getScenceNameByFileId(
      pointIdOverride || currentViewer.selectedPoint,
      points,
      selectedImageIdOverride || currentViewer.selectedImage
    )

    const sceneString = Krpano.generateSceneXML({
      sceneName,
      tilesUrl: tilesUrl || undefined,
      multires: multires || undefined,
      s3Path: imageUrl,
      thumbnailUrl: previewUrl || ''
    })

    const sceneStringFixed = sceneString.replace(/\r?\n|\r/g, ' ')
    krpano.set('scene_xml', sceneStringFixed)
    krpano.call(`includexmlstring(get(scene_xml), null, null, false)`)

    krpano.call(
      `load_scene(${sceneName},${currentH},${currentV},${currentZ},${!viewerLoading.firstLoading})`
    )

    if (typeof document !== 'undefined' && document.fonts?.ready) {
      try {
        await document.fonts.ready
      } catch {
        // continue
      }
    }

    viewerLoading.firstLoading &&
      addPropertiesToKrpano(
        krpano,
        annotations,
        directions,
        stairways,
        tripodUrl
      )

    setViewerLoading((prev) => ({
      ...prev,
      firstLoading: false
    }))
  }

  const embedpano = (
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    tripodUrl?: string,
    pointDetail?: Point,
    selectedImageIdOverride?: number
  ) => {
    const panoViewer = document.getElementById('embedpano-viewer') as any
    const target = document.getElementById('viewer-pano-container')
    let currentSelectedPoint = currentViewer.selectedPoint
    let currentSelectedImage =
      selectedImageIdOverride || currentViewer.selectedImage
    if (pointDetail) {
      if (!selectedImageIdOverride) {
        currentSelectedImage = pointDetail.image360?.id
      }
      currentSelectedPoint = pointDetail.id
    }
    const { imageUrl, previewUrl, multires, tilesUrl, h, v, z } =
      krpanoInitProps

    if (target) {
      // fist time only whne krpano not mount -> init krpano with current point and init properties
      if (!panoViewer) {
        ;(window as any).embedpano({
          id: 'embedpano-viewer',
          xml: null,
          target: 'viewer-pano-container',
          html5: 'auto+webgl',
          bgcolor: '#FFFFFF',
          onready: (krpano: any) => {
            krpanoOnreadyCallback(
              krpano,
              krpanoInitProps,
              annotations,
              directions,
              stairways,
              tripodUrl,
              currentSelectedPoint,
              currentSelectedImage
            )
          }
        })
      } else {
        const sceneName = Krpano.getScenceNameByFileId(
          currentSelectedPoint,
          points,
          currentSelectedImage
        )
        const currentSceneName = panoViewer.get('xml.scene')

        if (currentSceneName !== sceneName) {
          if (!currentViewer.isSelectPointInLeftMenu) {
            // call when krpano mounted and change point or click to direction
            if (panoViewer.get(`scene[${sceneName}]`) === null) {
              const sceneString = Krpano.generateSceneXML({
                sceneName,
                tilesUrl: tilesUrl || undefined,
                multires: multires || undefined,
                s3Path: imageUrl,
                thumbnailUrl: previewUrl || ''
              }).replace(/\r?\n|\r/g, ' ')

              panoViewer.set('scene_xml', sceneString)
              panoViewer.call(
                `includexmlstring(get(scene_xml), null, null, false)`
              )
            }
            // load init view
            panoViewer.call(`load_scene(${sceneName},${h},${v},${z})`)
          }
        }
        panoViewer.call(`remove_all_hotspot()`)
        addPropertiesToKrpano(
          panoViewer,
          annotations,
          directions,
          stairways,
          tripodUrl
        )
      }
    }
  }

  const handlePreloadScenes = async () => {
    const embedpano = document.getElementById('embedpano-viewer') as any
    const listPointTo = points
      .find((item) => item.id === currentViewer.selectedPoint)
      ?.directions?.map((item) => item.point_to)

    if (!listPointTo?.length) {
      return
    }

    await getSenceData(
      {
        method: 'post',
        data: {
          building_id: Number(id),
          floor_id: Number(currentViewer.selectedFloor),
          point_ids: listPointTo
        }
      },
      {
        onSuccess: (data: SenceDataResponse) => {
          try {
            const currentFileId = points.find(
              (p) => p.id === currentViewer.selectedPoint
            )?.image360?.id

            const items: SenceData[] = []

            data.data.forEach((item) => {
              if (
                embedpano.get(`scene[scene_${item.id}]`) === null &&
                item.id !== currentFileId
              ) {
                items.push(item)
              }
            })
            if (items.length) {
              const screenString = Krpano.generateSceneXMLs(items)
              embedpano.call(
                `includexmlstring(${screenString}, null, null, false)`
              )
            }
          } catch (error) {}
        },
        onError: () => {}
      }
    )
  }

  // get point detail when select point
  useSWR<Point>(
    currentViewer.selectedFloor &&
      currentViewer.selectedPoint &&
      building &&
      mapItems &&
      !isValidatingMapItems
      ? {
          url: `v1/points/${currentViewer.selectedPoint}`,
          params: {
            building_id: id,
            floor_id: currentViewer?.selectedFloor
          }
        }
      : null,
    {
      onSuccess: (data) => {
        try {
          const selectedImageId =
            (isFirstLoadingSubimage.current && urlSubimage
              ? +urlSubimage
              : currentViewer.selectedImage) || data?.image360?.id

          const selectedImageData =
            data?.subImages?.find((img) => img.id === selectedImageId) ||
            data?.image360

          setCurrentViewer((prev) => {
            return {
              ...prev,
              pointDetail: data,
              selectedFloor:
                data?.floorId ||
                currentViewer.selectedFloor ||
                prev.selectedFloor,
              selectedPoint: data?.id,
              selectedImage: selectedImageId
            }
          })

          const urlToUpdate = new URL(window.location.href)
          urlToUpdate.searchParams.set(
            'floor',
            (data?.floorId || currentViewer.selectedFloor)?.toString() || ''
          )
          urlToUpdate.searchParams.set('point', data?.id || '')
          if (data?.areaId) {
            urlToUpdate.searchParams.set('area', data.areaId.toString())
          }

          if (
            !currentViewer.isClickToDirection &&
            !currentViewer.isClickToNextFloor &&
            !isFirstLoadingSubimage.current
          ) {
            urlToUpdate.searchParams.delete('hlookat')
            urlToUpdate.searchParams.delete('vlookat')
            urlToUpdate.searchParams.delete('fov')
          }

          if (selectedImageId && selectedImageId !== data?.image360?.id) {
            urlToUpdate.searchParams.set('subimage', selectedImageId.toString())
          } else {
            urlToUpdate.searchParams.delete('subimage')
          }
          window.history.pushState({}, '', urlToUpdate.toString())

          isFirstLoadingSubimage.current = false

          if (selectedImageData) {
            const isInternalNav =
              currentViewer.isClickToDirection ||
              currentViewer.isClickToNextFloor

            let pov
            if (isInternalNav && currentPov) {
              pov = currentPov
            } else if (urlH && urlV && urlZ) {
              pov = { h: +urlH, v: +urlV, z: +urlZ }
            } else if (selectedImageData.id !== data?.image360?.id) {
              pov = selectedImageData.initView || data?.initView
            } else {
              pov = data?.initView
            }

            const initProps = {
              imageUrl: selectedImageData.s3Path,
              previewUrl: selectedImageData.multires
                ? `${selectedImageData.tilesUrl}preview.jpg`
                : selectedImageData.thumbnail,
              hgap: data?.initView?.radarHGap || 0,
              h: pov?.h || 0,
              v: pov?.v || 0,
              z: pov?.z || 100,
              multires: selectedImageData.multires,
              tilesUrl: selectedImageData.tilesUrl
            }

            setCurrentHgap(initProps.hgap)
            const annotationList = data?.annotations?.filter((item) => {
              if (item.imageID) {
                return item.imageID === selectedImageData.id
              }
              return selectedImageData.id === data.image360?.id
            })

            const stairList = data?.stairways?.map((item) => ({
              ...item,
              nextFloorName: building?.floors?.find(
                (floor) => floor.id === +(item.nextFloorId || 0)
              )?.name
            }))
            const tripodUrl = building?.extraInfo?.tripodImageUrl
            embedpano(
              initProps,
              annotationList,
              data?.directions,
              stairList,
              tripodUrl,
              data,
              selectedImageId
            )
            handlePreloadScenes()
          } else {
            Krpano.UnmountPanoViewer()
          }
        } catch (error) {
          console.error('[ViewerKrpano] Parse pointDetail error:', error)
        }
      }
    }
  )

  handleClickAnnotation()
  handleChangeFloor()

  useEffect(() => {
    ;(window as any).sync_current_pov = (h: number, v: number, z: number) => {
      setCurrentPov({ h, v, z })
      const currentId = currentViewer.pointDetail?.id
      if (currentId) {
        setPovPerPoint((prev) => ({
          ...prev,
          [currentId]: { h, v, z }
        }))
      }
    }
    return () => {
      delete (window as any).sync_current_pov
    }
  }, [setCurrentPov, setPovPerPoint, currentViewer.pointDetail?.id])

  useEffect(() => {
    return () => {
      Krpano.UnmountPanoViewer()
      setViewerLoading({ firstLoading: true })
    }
  }, [setViewerLoading])

  return {
    currentViewer,
    setCurrentViewer,
    currentPov,
    setCurrentHgap,
    embedpano
  }
}
